import { Component, inject, input, OnInit, signal } from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";
import saveAs from "file-saver";
import { filter } from "rxjs";

import { AdaaHelper, isDtkpi, isNtkpi, isSrvkpi } from "../../../../../../../core/utils";
import { Constants } from "../../../../../../constants/constants";
import type { PropTypeModelType, WorkflowDiffVersionType } from "../../../../../../models";
import { FilesApiService, PropertiesService } from "../../../../../../services";
import {
  AccordionDiffViewComponent,
  ActivityWorkloadDiffViewComponent,
  AttachmentDiffViewComponent,
  ExtendedFieldsDiffComponent,
  LinkedNationalStrategiesDiffViewComponent,
  ListDiffViewComponent,
  NormalDiffViewComponent,
  TargetsDiffViewComponent,
} from "../../_lib";
import type {
  AccordionSectionConfigType,
  CompositeSectionConfigType,
  ListSectionConfigType,
  ScreenSectionConfigType,
  ScreenType,
} from "../../wf-difference.types";
import { intpMilestoneScreenConfig, intpProjectScreenConfig } from "../intp-screen/intp-screen-config";
import { commonMetricValueScreenConfig } from "../metric-actual-value-screen/metric-actual-value-screen-config";
import { activitiesScreenConfig, newProjectsScreenConfig } from "./activities-config";
import {
  entityPillarsAuditScreenConfig,
  govSectorsAuditScreenConfig,
  simpleScreenConfig,
  transformationalTargetsScreenConfig,
} from "./config";
import { countriesScreenConfig } from "./countries-config";
import { projectOrdersScreenConfig } from "./country-project.order-config";
import { entityScreenConfig } from "./entity-config";
import { formulaScreenConfig } from "./formula-config";
import { measurementScopeScreenConfig } from "./measurement-scope-config";
import { operationsStrategicTasksScreenConfig } from "./operations-strategic-tasks-config";
import { transformationalAgreementsScreenConfig } from "./transformational-agreements.config";
import { userScreenConfig } from "./user-config";
import { visitsScreenConfig } from "./visits-config";

@Component({
  selector: "adaa-simple-screen",
  imports: [
    TranslatePipe,
    NormalDiffViewComponent,
    AttachmentDiffViewComponent,
    ExtendedFieldsDiffComponent,
    ActivityWorkloadDiffViewComponent,
    LinkedNationalStrategiesDiffViewComponent,
    ListDiffViewComponent,
    AccordionDiffViewComponent,
    TargetsDiffViewComponent,
  ],
  templateUrl: "./simple-screen.html",
  styleUrl: "./simple-screen.scss",
})
export class SimpleScreenComponent implements OnInit {
  private readonly _propertiesService = inject(PropertiesService);
  private readonly _filesApiService = inject(FilesApiService);

  showOnlyNew = input.required<boolean>();
  itemType = input<number>();
  actionTypeId = input<number>();
  newObject = input<WorkflowDiffVersionType>();
  oldObject = input<WorkflowDiffVersionType>();
  isNew = input<boolean>(false);
  viewOnlyDifferences = input<boolean>(false);

  isScreenReady = signal<boolean>(false);
  props = signal<PropTypeModelType[]>([]);

  readonly $asListConfig = ($s: CompositeSectionConfigType) => $s as ListSectionConfigType;
  readonly $asAccordionConfig = ($s: CompositeSectionConfigType) => $s as AccordionSectionConfigType;

  config: {
    type: ScreenType;
    sections: (ScreenSectionConfigType | AccordionSectionConfigType)[];
  };

  public ngOnInit() {
    this._initScreen();
    this._fetchConstants();
  }

  private _initScreen() {
    if (
      this.actionTypeId() === Constants.CONSTANT_AUDIT_ACTIONS_OPTIONS.SORT &&
      this.itemType() === Constants.CONSTANT_INTERNATIONAL_PROJECTS_COUNTRY
    ) {
      this.config = projectOrdersScreenConfig;
    } else {
      switch (this.itemType()) {
        case Constants.GOV_SCORES_SECTOR:
          this.config = govSectorsAuditScreenConfig;
          break;
        case Constants.GOV_SCORES_PILLAR:
          this.config = entityPillarsAuditScreenConfig;
          break;

        case Constants.GOV_SCORES_SCOPE:
          this.config = {
            type: "simple",
            sections: measurementScopeScreenConfig,
          };
          break;

        case Constants.CONSTANT_TRANSFORMATIONAL_TARGETS:
          this.config = transformationalTargetsScreenConfig;
          break;

        case Constants.METRICS_TYPE_ID.DIMENSION_OVERRIDE:
          this.config = commonMetricValueScreenConfig;
          break;

        case Constants.CONSTANT_ENTITY_OBJECT:
          this.config = entityScreenConfig;
          break;

        case Constants.CONSTANT_USER_OBJECT:
          this.config = userScreenConfig;
          break;

        case Constants.CONSTANT_FORMULA:
          this.config = formulaScreenConfig;
          break;

        case Constants.CONSTANT_PERMISSIONS.INTP_COUNTRY_VISITS_LOGS:
          this.config = visitsScreenConfig;
          break;

        case Constants.CONSTANT_INTERNATIONAL_PROJECTS_COUNTRY:
          this.config = countriesScreenConfig;
          break;

        case Constants.CONSTANT_INTERNATIONAL_PROJECTS:
          this.config = intpProjectScreenConfig;
          break;

        case Constants.CONSTANT_INTERNATIONAL_PROJECTS_MILESTONE:
          this.config = intpMilestoneScreenConfig;
          break;

        case Constants.ACTIVITY_WORKFLOW_DS3.STRATEGIC_ACTIVITY:
        case Constants.ACTIVITY_WORKFLOW_DS3.OPERATIONAL_ACTIVITY:
        case Constants.ACTIVITY_WORKFLOW_DS3.M1_MILESTONE:
        case Constants.ACTIVITY_TYPES_DS3.ACTIVITY:
        case Constants.ACTIVITY_TYPES_DS3.OPERATIONAL_ACTIVITY:
        case Constants.ACTIVITY_TYPES_DS3.M1_MILESTONE:
        case Constants.ACTIVITY_TYPES_DS3.STRATEGIC_ACTIVITY:
          this.config = activitiesScreenConfig;
          break;

        case Constants.PROJECT_WORKFLOW_DS3.REGULAR_TASK:
        case Constants.PROJECT_WORKFLOW_DS3.STRATEGIC_PROJECT:
        case Constants.PROJECT_WORKFLOW_DS3.M1:
        case Constants.PROJECT_TYPES_DS3.PROJECT:
        case Constants.PROJECT_TYPES_DS3.REGULAR_TASK:
        case Constants.PROJECT_TYPES_DS3.STRATEGIC_PROJECT:
        case Constants.PROJECT_TYPES_DS3.M1:
          this.config = newProjectsScreenConfig;
          break;

        case Constants.CONSTANT_TRANSFORMATIONAL_AGREEMENT:
          this.config = transformationalAgreementsScreenConfig;
          break;

        case Constants.OPERATIONS:
        case Constants.STRATEGIC_TASKS:
          this.config = operationsStrategicTasksScreenConfig;
          break;

        default:
          this.config = {
            type: "simple",
            sections: simpleScreenConfig,
          };
          break;
      }
    }
  }

  private _fetchConstants() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    const constantIds = [
      Constants.CONSTANT_MEASUREMENT_UNIT,
      Constants.CONSTANT_FREQUENCY,
      Constants.CONSTANT_TREND,
      Constants.CONSTANT_YTP,
    ];

    const kpiType = this.newObject()?.kpiType;

    if (kpiType != null) {
      if (isNtkpi(kpiType) || isDtkpi(kpiType)) {
        constantIds.push(Constants.CONSTANT_KPI_TARGET_TYPE);
      }
      if (isNtkpi(kpiType)) {
        constantIds.push(
          ...[Constants.CONSTANT_TARGET_CATEGORY, Constants.CONSTANT_PUBLISHED_STATUS, Constants.CONSTANT_MONTHS]
        );
      }
      if (isSrvkpi(kpiType)) {
        constantIds.push(...[Constants.CONSTANT_PERSPECTIVE_TYPE, Constants.CONSTANT_CLASSIFICATION_TYPE]);
      }
    }

    this._propertiesService
      .getPropByIdList(constantIds)
      .pipe(filter((res) => !res.inError))
      .subscribe({
        next: (res) => {
          this.props.set(res.responseData ?? []);

          for (const section of this.config.sections) {
            if (section.view !== "normal" && section.view !== "list") continue;

            switch ((section as ScreenSectionConfigType).field) {
              case "targetCategory":
                (section as ScreenSectionConfigType).format = (data: { targetCategory: number }) =>
                  that._getTextFromProp(data.targetCategory, res.responseData);
                break;

              case "targetTypeId":
                (section as ScreenSectionConfigType).format = (data: { targetTypeId: number }) =>
                  that._getTextFromProp(data.targetTypeId, res.responseData);
                break;

              case "publishedState":
                (section as ScreenSectionConfigType).format = (data: { publishedState: number }) =>
                  that._getTextFromProp(data.publishedState, res.responseData);
                break;

              case "actualDataAvailabilityPeriod":
                (section as ScreenSectionConfigType).format = (data: { actualDataAvailabilityPeriod: number }) =>
                  that._getTextFromProp(data.actualDataAvailabilityPeriod, res.responseData);
                break;

              case "ytpCalc":
                (section as ScreenSectionConfigType).format = (data: { ytpCalc: number }) =>
                  that._getTextFromProp(data.ytpCalc, res.responseData);
                break;

              case "trend":
                (section as ScreenSectionConfigType).format = (data: { trend: number }) =>
                  that._getTextFromProp(data.trend, res.responseData);
                break;

              case "dataType":
                (section as ScreenSectionConfigType).format = (data: { dataType: number }) =>
                  that._getTextFromProp(data.dataType, that.props() || []);
                break;

              case "measurementUnit":
                (section as ScreenSectionConfigType).format = (data: { measurementUnit: number }) =>
                  that._getTextFromProp(data.measurementUnit, res.responseData);
                break;

              case "frequency":
                (section as ScreenSectionConfigType).format = (data: { frequency: number }) =>
                  that._getTextFromProp(data.frequency, res.responseData);
                break;

              case "perspective":
                (section as ScreenSectionConfigType).format = (data: { perspective: number }) =>
                  that._getTextFromProp(data.perspective, res.responseData);
                break;

              case "classification":
                (section as ScreenSectionConfigType).format = (data: { classification: number }) =>
                  that._getTextFromProp(data.classification, res.responseData);
                break;

              default:
                break;
            }
          }

          this.isScreenReady.set(true);
        },
      });
  }

  private _getTextFromProp(id: number, list: { id: number }[]) {
    const data = list.find((e) => e.id === id);
    if (data) {
      return AdaaHelper.getItemValueByToken(data, "name");
    }
    return "";
  }

  public download(data: Record<string, unknown>) {
    data.downloading = true;
    if (data.itemAttachmentId) {
      this._filesApiService.download(data.itemAttachmentId as number, true).subscribe({
        next: (blob) => {
          saveAs(blob, data.fileName as string);
        },
      });
    } else {
      this._filesApiService.download(data.id as number, true).subscribe({
        next: (blob) => {
          saveAs(blob, data.filename as string);
        },
        error: () => {
          data.downloading = false;
        },
        complete: () => {
          data.downloading = false;
        },
      });
    }
  }
}
